"use client";

import { useEffect, useState } from "react";

export interface ReviewsAggregate {
  avgRating: number;
  totalReviews: number;
}

// Module-scoped single-flight cache. Three components on the home (hero
// chip, explore stats, testimonials header) call this hook independently —
// without dedup that was 3 identical requests on every page load. We share
// one in-flight promise and one resolved value across all consumers.
let cachedAggregate: ReviewsAggregate | null = null;
let inflight: Promise<ReviewsAggregate | null> | null = null;

const fetchAggregate = (): Promise<ReviewsAggregate | null> => {
  if (cachedAggregate) return Promise.resolve(cachedAggregate);
  if (inflight) return inflight;
  const base = process.env.NEXT_PUBLIC_SOCKET || "";
  if (!base) return Promise.resolve(null);
  inflight = fetch(`${base}/api/reviews-aggregate`, {
    headers: { Accept: "application/json" },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || typeof data.avgRating !== "number") return null;
      cachedAggregate = {
        avgRating: data.avgRating,
        totalReviews: data.totalReviews ?? 0,
      };
      return cachedAggregate;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/**
 * Site-wide reviews aggregate (avg rating + total count). Resolves from a
 * module-scoped single-flight cache so multiple consumers on the same page
 * share one network call.
 *
 * Returns `null` while in flight or on error so callers can decide whether
 * to render a placeholder or hide the chip entirely.
 */
export function useReviewsAggregate(): ReviewsAggregate | null {
  const [aggregate, setAggregate] = useState<ReviewsAggregate | null>(
    cachedAggregate
  );

  useEffect(() => {
    if (cachedAggregate) {
      setAggregate(cachedAggregate);
      return undefined;
    }
    let aborted = false;
    fetchAggregate().then((data) => {
      if (!aborted && data) setAggregate(data);
    });
    return () => {
      aborted = true;
    };
  }, []);

  return aggregate;
}
